import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../../hooks';
import type { BaseModalProps } from '../../Modal.types';
import { ModalSizesStyles } from '../../Modal.sizes';

export const ModalContainer = (props: ModalContainerProps) => {
  const {
    children,
    style,
    offset,
    size = 'small',
    fitHeight = false,
    ...restProps
  } = props;

  const { match } = useResponsive();

  const responsiveStyles = match([styles.dialogMobile, styles.dialogTabletUp]);

  const sizeSpecificStyles = ModalSizesStyles[size];
  const xsFullWidthStyle = match([styles.dialogMobileFullWidth, undefined]);
  const fitHeightStyles = fitHeight ? styles.dialogFitHeight : undefined;

  const offsetStyle = useStyle(() => {
    if (!offset) return;

    const formattedOffset = Number.parseInt(offset, 10);
    const sizeAfterOffset = `${100 - formattedOffset}%`;

    return {
      width: sizeAfterOffset,
      height: sizeAfterOffset,
      maxHeight: '100%',
    };
  }, [offset]);

  return (
    <View
      style={[
        styles.dialog,
        responsiveStyles,
        sizeSpecificStyles,
        xsFullWidthStyle,
        fitHeightStyles,
        offsetStyle,
        style,
      ]}
      pointerEvents="box-none"
      {...restProps}
    >
      {children}
    </View>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type ModalContainerProps = ViewProps &
  Pick<BaseModalProps, 'size' | 'fitHeight' | 'offset'>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  dialog: {
    maxWidth: '100%',
    marginHorizontal: 'auto',
    zIndex: theme.zIndex.modal,
  },
  dialogMobile: {
    marginTop: 'auto',
    paddingTop: 80,
  },
  dialogMobileFullWidth: {
    width: '100%',
  },
  dialogTabletUp: {
    marginVertical: 'auto',
    paddingVertical: 80,
  },
  dialogFitHeight: {
    maxHeight: '100%',

    // NOTE: In order to override all possible cases of padding
    paddingTop: 0,
    paddingBottom: 0,
    paddingVertical: 0,
  },
});
